'use client';

import { useState } from 'react';
import {
  ArrowLeft,
  LayoutDashboard,
  Factory,
  ClipboardList,
  Settings,
  Package,
} from 'lucide-react';
import { ManufacturingDashboard } from './components/ManufacturingDashboard';
import { BOMListPage } from './components/BOMList';
import { WorkOrderListPage } from './components/WorkOrderList';
import { WorkstationListPage } from './components/WorkstationList';
import { StockEntryListPage } from './components/StockEntryList';

export type ManufacturingPage =
  | 'dashboard'
  | 'bom'
  | 'work-orders'
  | 'workstations'
  | 'stock-entry';

interface ManufacturingModuleProps {
  onBack?: () => void;
}

const menuItems: { id: ManufacturingPage; label: string; icon: typeof LayoutDashboard }[] = [
  { id: 'dashboard', label: '대시보드', icon: LayoutDashboard },
  { id: 'bom', label: 'BOM', icon: Factory },
  { id: 'work-orders', label: '작업지시', icon: ClipboardList },
  { id: 'workstations', label: '작업장', icon: Settings },
  { id: 'stock-entry', label: '입출고', icon: Package },
];

export function ManufacturingModule({ onBack }: ManufacturingModuleProps) {
  const [currentPage, setCurrentPage] = useState<ManufacturingPage>('dashboard');

  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <ManufacturingDashboard onNavigate={setCurrentPage} />;
      case 'bom':
        return <BOMListPage />;
      case 'work-orders':
        return <WorkOrderListPage />;
      case 'workstations':
        return <WorkstationListPage />;
      case 'stock-entry':
        return <StockEntryListPage />;
      default:
        return <ManufacturingDashboard onNavigate={setCurrentPage} />;
    }
  };

  return (
    <div className="flex flex-col h-full -m-6">
      {/* 상단 네비게이션 */}
      <div className="flex items-center gap-2 px-4 py-3 bg-card border-b border-border">
        {onBack && (
          <button
            onClick={onBack}
            className="p-2 rounded-lg hover:bg-accent"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
        )}
        <h1 className="text-xl font-bold mr-6">제조 관리</h1>

        {/* 탭 메뉴 */}
        <div className="flex items-center gap-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = currentPage === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setCurrentPage(item.id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden sm:inline">{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 페이지 컨텐츠 */}
      <div className="flex-1 overflow-auto p-6">
        {renderPage()}
      </div>
    </div>
  );
}
